import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useQuery as useQuery$1, useClient, useMutation as useMutation$1 } from 'urql';
import { useQueries as useQueries$1 } from '@tanstack/react-query';
import { isEqual } from 'lodash-es';
import { useDeepMemo } from '../utils/deep-memo.js';
import { usePolling, useRetryWithBackoff } from '../utils/timers.js';

/**
 * Urql's useQuery hook, but with some extra arguments/options for polling and
 * auto retry of failed queries.
 */ function useQuery(args) {
    const [state, refetch] = useQuery$1(args);
    // Callback to force urql to reload data from the server, skipping the cache
    const refetchFromNetwork = useCallback((opts)=>{
        refetch({
            requestPolicy: 'network-only',
            ...opts
        });
    }, [
        refetch
    ]);
    // Fetching will be true for initial load and when we have done a refetch
    // with 'network-only'. Stale will be true when urql is reloading data
    // because the cached data was invalidated by a mutation
    const requestInProgress = state.fetching || state.stale;
    // Poll if configured, disabling polling while paused, a request is in
    // progress, or while we're in an error state
    let pollInterval = args.pollInterval;
    if (args.pause === true || requestInProgress || state.error !== undefined) {
        pollInterval = undefined;
    }
    usePolling(refetchFromNetwork, pollInterval);
    // Automatically retry errors if configured, passing undefined for the error
    // to retry if paused or not configured
    let errorToRetry = state.error;
    if (args.pause === true || args.autoRetryErrors !== true) {
        errorToRetry = undefined;
    }
    const secondsUntilRetry = useRetryWithBackoff(refetchFromNetwork, requestInProgress, errorToRetry);
    // Add our extra piece of state to the urql state and return response
    const response = useMemo(()=>{
        return [
            {
                ...state,
                secondsUntilRetry
            },
            refetchFromNetwork
        ];
    }, [
        state,
        refetchFromNetwork,
        secondsUntilRetry
    ]);
    return response;
}
/**
 * Custom implementation using react-query's useQueries that allows execution of parallel dynamic queries.
 * Each queries object accepts a `queryArgs` attribute in place of `queryFn`.
 */ function useQueries({ queries, context }) {
    const client = useClient();
    const mappedQueries = queries.map(({ onResponseSuccess = (response)=>response, queryArgs, ...rest })=>{
        return {
            ...rest,
            queryFn: async ()=>{
                const response = await client.query(queryArgs.query, queryArgs.variables, queryArgs.context).toPromise();
                return onResponseSuccess(response);
            }
        };
    });
    return useQueries$1({
        queries: mappedQueries,
        context: context
    });
}
/**
 * Just like urql's useMutation, but will throw when executing the mutation if
 * it results in an Error. (Errors thrown will be an urql CombinedError).
 */ function useMutation(query) {
    const [state, callback] = useMutation$1(query);
    // Wrap the callback that executes the mutation and throw the error if the
    // mutation results in an error
    const wrapped = async (...execArgs)=>{
        const result = await callback(...execArgs);
        if (result.error) {
            throw result.error;
        }
        return result;
    };
    return [
        state,
        wrapped
    ];
}
const INITIAL_QUERY_STATE = {
    fetching: false
};
/**
 * An urql version of Apollo's useLazyQuery hook, for manually invoking a
 * query and supplying variable values at execution time.
 */ // eslint-disable-next-line @typescript-eslint/ban-types
function useLazyQuery(args) {
    const client = useClient();
    const [queryState, setQueryState] = useState(INITIAL_QUERY_STATE);
    const inFlightAbort = useRef();
    args = useDeepMemo(()=>args, [
        args
    ]);
    // Cancel any inflight requests and reset state if hook inputs change and on unmount
    useEffect(()=>{
        return ()=>{
            if (inFlightAbort.current !== undefined) {
                inFlightAbort.current.abort();
                inFlightAbort.current = undefined;
            }
            setQueryState(INITIAL_QUERY_STATE);
        };
    }, [
        args
    ]);
    // Callback to kick off the query
    const executeQuery = useCallback(async (execArgs)=>{
        // Cancel any in-flight requests
        if (inFlightAbort.current !== undefined) {
            inFlightAbort.current.abort();
        }
        // Re-initialize abort state
        const abortController = new AbortController();
        inFlightAbort.current = abortController;
        setQueryState({
            fetching: true,
            error: undefined,
            data: undefined
        });
        // Attach abort controller to request context
        const ctx = {
            ...execArgs.context
        };
        ctx.fetchOptions ??= {};
        if (typeof ctx.fetchOptions === 'function') {
            ctx.fetchOptions = ctx.fetchOptions();
        }
        ctx.fetchOptions.signal = abortController.signal;
        // Network errors come back in result.error along with GraphQL errors, so
        // no need to try/catch here
        const result = await client.query(args.query, execArgs.variables, execArgs.context).toPromise();
        // Only do the side-effect to set state if we haven't been aborted
        if (abortController.signal.aborted === false) {
            setQueryState({
                fetching: false,
                error: result.error,
                data: result.data
            });
        }
    }, [
        client,
        args
    ]);
    return [
        queryState,
        executeQuery
    ];
}
const defaultGetPaginationVariables = ({ after, pageSize })=>({
        after,
        pageSize
    });
/**
 * usePaginatedQuery() is a hook to help manage server side GraphQL
 * queries that return a cursor-based pagination response. It handles translating
 * page numbers to cursors, and provides a way to fetch the next and previous page of data.
 */ function usePaginatedQuery({ query, defaultPageSize = 100, additionalVariables, getPaginationData, getPaginationVariables = defaultGetPaginationVariables, pause = false }) {
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [page, setPage] = useState(0);
    const resetCursor = ()=>{
        setAllCursors([
            null
        ]);
        setPage(0);
    };
    const updatePageSize = useCallback((newPageSize)=>{
        setPageSize(newPageSize);
        resetCursor();
    }, []);
    /**
   * Handles updating the page and pageSize together, which is a common pattern
   * when using with v6 MUI datagrid.
   */ const setPageState = (newState)=>{
        if (newState.pageSize !== pageSize) {
            updatePageSize(newState.pageSize);
            return;
        }
        if (newState.page !== page) {
            setPage(newState.page);
        }
    };
    const addCursorToMemory = (cursor)=>{
        if (!allCursors.includes(cursor)) {
            setAllCursors(allCursors.concat(cursor));
        }
    };
    const [allCursors, setAllCursors] = useState([
        null
    ]);
    const currentCursor = allCursors[page];
    const paginationVariables = getPaginationVariables({
        pageSize,
        after: currentCursor
    });
    const [{ data, fetching, error, stale }] = useQuery({
        query,
        variables: {
            ...paginationVariables,
            ...additionalVariables
        },
        pause
    });
    const { rows = [], rowCount = 0, cursor = null, hasNextPage = false } = data ? getPaginationData(data) : {};
    useEffect(()=>{
        if (cursor) {
            addCursorToMemory(cursor);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        cursor
    ]);
    return {
        pageSize,
        setPageSize: updatePageSize,
        page,
        setPage,
        setPageState,
        fetching,
        error,
        rows,
        rowCount,
        cursor: currentCursor,
        endCursor: cursor,
        hasNextPage,
        stale,
        /** Convenience function for consumers to reset the cursor list, e.g. if filters change and the previously set
     * cursors are no longer valid. Also sets the page to 0.
     */ resetCursor
    };
}
const DEFAULT_PAGE_SIZE = 100;
/**
 * usePaginatedQueryGetFullList() is a hook to return all data from server side GraphQL queries that return a
 * cursor-based pagination response. It iterates through all pages, translating page numbers
 * to cursors. All results are concatenated together and the entire result is returned.
 */ function usePaginatedQueryGetFullList({ query, additionalVariables, defaultPageSize = DEFAULT_PAGE_SIZE, getPaginationData, maxFetchCount = Infinity, pause = false }) {
    const [variables, setVariables] = useState({
        after: '',
        pageSize: defaultPageSize,
        filters: null
    });
    const { rows, rowCount, endCursor, hasNextPage, fetching: loading, error, stale } = usePaginatedQuery({
        query,
        getPaginationData,
        pause: variables.pageSize === 0 || pause,
        additionalVariables: {
            ...variables,
            ...additionalVariables
        }
    });
    const results = useRef([]);
    const mergedResults = useRef([]);
    const fetchCount = useRef(0);
    const isLoading = useRef(false);
    const isStale = useRef(false);
    /**
   * Checking to see if a fetch was just made before setting data (the same data we may already have)
   */ const completedFetch = !loading && isLoading.current;
    const completedRefetch = !stale && isStale.current;
    if ((completedFetch || completedRefetch) && !isEqual(rows, mergedResults.current)) {
        fetchCount.current = fetchCount.current + 1;
        /**
     * If the variable 'after' is set to an emtpy string, we know that we are on
     * the first page of a paginated list. This means that we should replace any existing
     * items with the new results. Otherwise, we'll append to the existing list.
     */ if (variables.after === '') {
            results.current = rows;
        } else {
            results.current = results.current.concat(rows);
        }
        mergedResults.current = rows;
    }
    if (isLoading.current !== loading) {
        isLoading.current = loading;
    }
    if (isStale.current !== stale) {
        isStale.current = stale;
    }
    useEffect(()=>{
        if (!loading && !stale && hasNextPage && endCursor && endCursor !== variables.after && fetchCount.current < maxFetchCount) {
            setVariables({
                ...variables,
                after: endCursor
            });
        }
    }, [
        endCursor,
        hasNextPage,
        loading,
        maxFetchCount,
        stale,
        variables
    ]);
    return {
        results: results.current,
        loading: fetchCount.current < maxFetchCount && (hasNextPage || loading || stale),
        totalCount: rowCount ?? 0,
        error
    };
}

export { DEFAULT_PAGE_SIZE, useLazyQuery, useMutation, usePaginatedQuery, usePaginatedQueryGetFullList, useQueries, useQuery };
//# sourceMappingURL=graphql.js.map
